/**
 * Roles and teams for Blood Moon (Werewolf / Villager / Seer).
 * Kept intentionally small; future games add their own role sets — shared
 * helpers must never assume exactly two teams (see plan §Engine generalization).
 */
// WEREWOLF/SEER/VILLAGER are Blood Moon. DECEIVER/PALADIN are The Whisper
// (see THE_WHISPER_SPEC.md §3); VILLAGER is shared by both games. Blood Moon
// never deals DECEIVER/PALADIN; The Whisper never deals WEREWOLF/SEER.
// THIEF/JOKER are Blood Moon: One Night, which re-deals WEREWOLF/SEER/VILLAGER
// alongside them (it is a Blood Moon variant, so it shares that game's roles).
export const ROLE_IDS = [
    'WEREWOLF',
    'SEER',
    'VILLAGER',
    'DECEIVER',
    'PALADIN',
    'THIEF',
    'JOKER',
];
export const RoleIds = {
    WEREWOLF: 'WEREWOLF',
    SEER: 'SEER',
    VILLAGER: 'VILLAGER',
    DECEIVER: 'DECEIVER',
    PALADIN: 'PALADIN',
    THIEF: 'THIEF',
    JOKER: 'JOKER',
};
export const TEAMS = ['VILLAGE', 'WOLVES'];
export const Teams = {
    VILLAGE: 'VILLAGE',
    WOLVES: 'WOLVES',
};
/**
 * Which team a role belongs to. The Whisper does NOT use the two-team win/parity
 * model for its GAMEPLAY (the Deceiver is a hidden traitor resolved by the HUNT
 * vote + scoring matrix in the Whisper engine, not by `teamOf`) — but it DOES
 * reuse `Team` for the one-round END verdict: DECEIVER maps to WOLVES so the
 * shared EndScreen's personal win/lose framing (and red-row tinting) works for
 * free (word found → 'VILLAGE' wins; candle died → 'WOLVES' wins, i.e. the
 * Deceiver-analog). PALADIN/VILLAGER stay VILLAGE. Blood Moon (the only game
 * reading `teamOf`/`alignmentIsWerewolf` for its OWN win check) never deals
 * DECEIVER/PALADIN, so its behavior is unchanged.
 *
 * One Night's THIEF/JOKER are VILLAGE — the team of the CARD, which is what
 * `teamOf` answers. In One Night a player's team follows the card they HOLD at
 * dawn, not the one they were dealt, so a Thief who stole the Werewolf card is
 * on WOLVES via that card; `teamOf(THIEF)` is never consulted for them. The One
 * Night win check reads final-card teams, so this mapping only ever describes an
 * unswapped Thief/Joker, who really are villagers.
 */
export const ROLE_TEAM = {
    WEREWOLF: 'WOLVES',
    SEER: 'VILLAGE',
    VILLAGER: 'VILLAGE',
    DECEIVER: 'WOLVES',
    PALADIN: 'VILLAGE',
    THIEF: 'VILLAGE',
    JOKER: 'VILLAGE',
};
export function teamOf(role) {
    return ROLE_TEAM[role];
}
/** Seer alignment read: true iff the target is on the wolves' side. */
export function alignmentIsWerewolf(role) {
    return teamOf(role) === 'WOLVES';
}
export const ROLE_META = {
    WEREWOLF: {
        id: 'WEREWOLF',
        label: 'Werewolf',
        glyph: '🐺',
        team: 'WOLVES',
        tagline: 'Hunt with your pack. Win when wolves reach parity.',
        onboarding: 'Each night, you and your packmates agree on one victim. By day, blend in and steer the vote. You win when wolves equal or outnumber the rest.',
    },
    SEER: {
        id: 'SEER',
        label: 'Seer',
        glyph: '🔮',
        team: 'VILLAGE',
        tagline: 'Each night, uncover one truth. Win when all wolves fall.',
        onboarding: 'Each night you investigate one player and privately learn whether they are a Werewolf. Guide the village without giving yourself away too soon.',
    },
    VILLAGER: {
        id: 'VILLAGER',
        label: 'Villager',
        glyph: '🕯️',
        team: 'VILLAGE',
        tagline: 'Find the wolves. Win when every wolf is gone.',
        onboarding: 'You have no night power — only your voice and your vote. Listen, deduce, and lynch the wolves before they reach parity.',
    },
    // ── The Whisper roles (glyph is a ceremony-glyph name, not emoji — the client
    //    maps it via ui/Glyph). See THE_WHISPER_SPEC.md §3/§8. ──────────────────
    DECEIVER: {
        id: 'DECEIVER',
        label: 'Deceiver',
        glyph: 'seerEye',
        team: 'VILLAGE',
        tagline: 'You know the word. Burn the candle before they say it.',
        onboarding: 'You secretly see the Seer’s word. Blend in — ask, guess, and steer the table wrong. Flood the queue to burn the candle, but never look too eager, or the hunt will catch you.',
    },
    PALADIN: {
        id: 'PALADIN',
        label: 'Paladin',
        glyph: 'candle',
        team: 'VILLAGE',
        tagline: 'Protect the hunt. Stay a suspect’s hand once each round.',
        onboarding: 'Once per round you may stay a player’s hand — wiping their queue and freezing them for a spell. Shield the innocent, choke the saboteur, and stay hidden while you do it.',
    },
    // ── Blood Moon: One Night roles. Both act ONCE, during the single night, and
    //    change whose card is whose — so at dawn nobody can trust the card they
    //    were dealt. Glyphs are ceremony-glyph names (client maps via ui/Glyph).
    THIEF: {
        id: 'THIEF',
        label: 'Thief',
        glyph: 'topHat',
        team: 'VILLAGE',
        tagline: 'Take another’s face. You become whatever you steal.',
        onboarding: 'In the night you may swap your card with another player’s, then look at your new one. They never learn. You win with whatever team you ENDED as — steal the wolf and you hunt with the pack.',
    },
    JOKER: {
        id: 'JOKER',
        label: 'Joker',
        glyph: 'seerEye',
        team: 'VILLAGE',
        tagline: 'Swap two others. Never learn what you did.',
        onboarding: 'In the night you may swap two OTHER players’ cards without looking at either. You stay a villager — but two people at the table are now wrong about themselves, and only you know who.',
    },
};
//# sourceMappingURL=roles.js.map